import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUp, ArrowDown, Eye, Send, RotateCcw, X, RefreshCw, ExternalLink, Crop } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageUploader } from "./ImageUploader";
import { ImagePositioner } from "./ImagePositioner";
import { PhotoListEditor } from "./PhotoListEditor";
import { imgSrc } from "@/lib/img";
import { FONT_PAIRINGS } from "@/lib/fonts";
import { updateContent, publishContent, discardDraft, contentStatus } from "@/lib/api";

const Field = ({ label, ...props }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input className="mt-1" {...props} />
  </div>
);

const Block = ({ title, children }) => (
  <div className="bg-[var(--card)] rounded-xl p-6 border border-[var(--border)] space-y-4">
    <h3 className="font-serif text-xl text-[var(--burgundy)]">{title}</h3>
    {children}
  </div>
);

const defaultVenue = { name: "", address: "", map_embed: "", map_link: "", directions_note: "" };
const defaultCommunity = { enabled: false, title: "", subtitle: "", description: "", nudge: "", activities: [] };
const defaultTheme = { primary: "#B8860B", secondary: "#6B1D2F", background: "#FAF7F2", surface: "#F5EFE6", accent: "#FBEAF0", font_pairing: "pairing_1" };
const defaultFaq = { enabled: true, title: "Good to Know", items: [] };
const defaultContacts = { enabled: true, title: "Questions? Reach us", note: "", people: [] };
const defaultRumi = { enabled: true, name: "Rumi", title: "Rumi's Corner", subtitle: "our best girl", description: "", image: "", photos: [] };
const defaultSections = { story: true, events: true, travel: true, gallery: true };
const defaultSectionOrder = ["events", "rsvp", "story", "rumi", "travel", "gallery", "guestbook", "suggestions", "faq", "community"];

// Every toggleable page section shown in the unified "Section Visibility" panel.
// type "sections" -> stored in content.sections[key]; type "enabled" -> stored in content[group].enabled
const SECTION_TOGGLES = [
  { key: "events", label: "Events", type: "sections" },
  { key: "story", label: "Our Story", type: "sections" },
  { key: "rumi", label: "Rumi's Corner", type: "enabled", group: "rumi" },
  { key: "travel", label: "Getting There", type: "sections" },
  { key: "gallery", label: "Gallery", type: "sections" },
  { key: "guestbook", label: "Guestbook", type: "enabled", group: "guestbook" },
  { key: "suggestions", label: "Community Events", type: "enabled", group: "suggestions" },
  { key: "faq", label: "FAQ", type: "enabled", group: "faq" },
  { key: "community", label: "Open-mic", type: "enabled", group: "community" },
];
const sectionOrderLabels = { story: "Our Story", rumi: "Rumi's Corner", events: "Events", travel: "Getting There & Directions", gallery: "Gallery", faq: "FAQ", community: "Community & Open Mic", rsvp: "RSVP", guestbook: "Guestbook", suggestions: "Community Events (idea wall)" };
const defaultRsvpSuccess = { title: "You're on the list! 💛", message: "" };
const defaultGuestbook = { enabled: true, title: "Guestbook", subtitle: "leave a note", description: "", moderation: false };
const defaultSuggestions = { enabled: false, title: "Help Shape the Celebration", subtitle: "your ideas", description: "", moderation: false };
const defaultHero = { background_image: "", subtitle: "", location_label: "", directions_label: "", overlay_opacity: 0.55, bg_position: "center", bg_zoom: 1 };
const defaultTravel = { title: "", description: "", items: [] };

const ColorField = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer shrink-0" />
    <div className="flex-1">
      <Label className="text-xs">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />
    </div>
  </div>
);

const normalize = (content) => ({
  ...content,
  hero: { ...defaultHero, ...(content.hero || {}) },
  venue: { ...defaultVenue, ...(content.venue || {}) },
  community: { ...defaultCommunity, ...(content.community || {}) },
  theme: { ...defaultTheme, ...(content.theme || {}) },
  rsvp_extra_fields: Array.isArray(content.rsvp_extra_fields) ? content.rsvp_extra_fields : [],
  events: Array.isArray(content.events) ? content.events : [],
  gallery: Array.isArray(content.gallery) ? content.gallery : [],
  travel: { ...defaultTravel, ...(content.travel || {}), items: Array.isArray(content.travel?.items) ? content.travel.items : [] },
  faq: { ...defaultFaq, ...(content.faq || {}), items: Array.isArray(content.faq?.items) ? content.faq.items : [] },
  contacts: { ...defaultContacts, ...(content.contacts || {}), people: Array.isArray(content.contacts?.people) ? content.contacts.people : [] },
  rumi: { ...defaultRumi, ...(content.rumi || {}) },
  sections: { ...defaultSections, ...(content.sections || {}) },
  section_order: content.section_order?.length ? content.section_order : defaultSectionOrder,
  rsvp_map_position: content.rsvp_map_position || "left",
  accommodation_info: content.accommodation_info ?? "",
  rsvp_success: { ...defaultRsvpSuccess, ...(content.rsvp_success || {}) },
  guestbook: { ...defaultGuestbook, ...(content.guestbook || {}) },
  suggestions: { ...defaultSuggestions, ...(content.suggestions || {}) },
  story: {
    mode: "classic", title: "Our Story", image1: "", image2: "",
    ...(content.story || {}),
    paragraphs: Array.isArray(content.story?.paragraphs) ? content.story.paragraphs : [],
    photos: Array.isArray(content.story?.photos) ? content.story.photos : [],
    timeline: Array.isArray(content.story?.timeline) ? content.story.timeline : [],
  },
});

export const ContentEditor = ({ content, onSaved }) => {
  const [c, setC] = useState(() => normalize(content));
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [changed, setChanged] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [heroAdjust, setHeroAdjust] = useState(false);

  const refreshStatus = () =>
    contentStatus()
      .then((s) => { setHasChanges(!!s.has_changes); setChanged(s.changed || []); })
      .catch(() => {});

  useEffect(() => { refreshStatus(); }, []);

  const previewUrl = `${window.location.origin}/?preview=1`;
  const openPreview = () => { setPreviewKey((k) => k + 1); setPreviewOpen(true); };

  const set = (path, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const moveSection = (idx, dir) => {
    setC((prev) => {
      const order = [...(prev.section_order || defaultSectionOrder)];
      const j = idx + dir;
      if (j < 0 || j >= order.length) return prev;
      [order[idx], order[j]] = [order[j], order[idx]];
      return { ...prev, section_order: order };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await updateContent(c);
      onSaved?.(saved);
      setC(normalize(saved));
      setPreviewKey((k) => k + 1);
      await refreshStatus();
      toast.success("Draft saved — preview it, then publish when you're ready.");
    } catch {
      toast.error("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await publishContent();
      await refreshStatus();
      setConfirmOpen(false);
      toast.success("Published! Your changes are now live for guests. 🎉");
    } catch {
      toast.error("Failed to publish.");
    } finally {
      setPublishing(false);
    }
  };

  const discard = async () => {
    setDiscarding(true);
    try {
      const live = await discardDraft();
      setC(normalize(live));
      onSaved?.(live);
      await refreshStatus();
      setDiscardOpen(false);
      toast.success("Draft discarded — back to what guests currently see.");
    } catch {
      toast.error("Failed to discard draft.");
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-content-editor">
      <div className={`sticky top-[72px] z-20 rounded-xl border-2 p-4 shadow-sm ${hasChanges ? "border-[var(--border-gold)] bg-[var(--surface)]" : "border-[var(--border)] bg-[var(--card)]"}`} data-testid="cms-publish-bar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${hasChanges ? "bg-[var(--gold)]" : "bg-green-500"}`} />
            <span className="text-[var(--text-secondary)]" data-testid="cms-draft-status">
              {hasChanges
                ? "You have unpublished changes — preview them, then publish to make them live for guests."
                : "Everything is published. Your site is live and up to date."}
            </span>
          </div>
          {hasChanges && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cms-publish-actions">
              <button onClick={openPreview} className="rounded-full px-4 py-2 text-sm font-medium border border-[var(--border)] text-[var(--text-primary)] flex items-center gap-1.5 hover:border-[var(--border-gold)]" data-testid="cms-preview-toggle">
                <Eye className="w-4 h-4" /> Preview
              </button>
              <button onClick={() => setDiscardOpen(true)} disabled={discarding} className="rounded-full px-4 py-2 text-sm font-medium border border-[var(--border)] text-[var(--text-secondary)] flex items-center gap-1.5 disabled:opacity-40 hover:border-red-300 hover:text-red-500" data-testid="cms-discard-btn">
                <RotateCcw className="w-4 h-4" /> Discard
              </button>
              <button onClick={() => setConfirmOpen(true)} disabled={publishing} className="rounded-full px-5 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-40" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="cms-publish-btn">
                <Send className="w-4 h-4" /> Publish
              </button>
            </div>
          )}
        </div>
        {hasChanges && changed.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--border)] flex flex-wrap items-center gap-1.5" data-testid="cms-changed-summary">
            <span className="text-xs font-semibold text-[var(--burgundy)] mr-1">What changed:</span>
            {changed.map((lbl) => (
              <span key={lbl} className="text-xs rounded-full px-2.5 py-1 bg-[var(--rose)] text-[var(--burgundy)] border border-[var(--border-gold)]" data-testid="cms-changed-chip">{lbl}</span>
            ))}
          </div>
        )}
      </div>
      <Block title="Couple & Date">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Partner 1" value={c.couple.partner1} onChange={(e) => set(["couple", "partner1"], e.target.value)} data-testid="cms-partner1-input" />
          <Field label="Partner 2" value={c.couple.partner2} onChange={(e) => set(["couple", "partner2"], e.target.value)} data-testid="cms-partner2-input" />
          <Field label="Wedding Date & Time (ISO)" value={c.wedding_date} onChange={(e) => set(["wedding_date"], e.target.value)} placeholder="2026-12-12T18:00:00" data-testid="cms-date-input" />
          <Field label="RSVP Deadline" type="date" value={c.rsvp_deadline} onChange={(e) => set(["rsvp_deadline"], e.target.value)} data-testid="cms-deadline-input" />
          <Field label="Date Label (shown on hero)" value={c.date_label || ""} onChange={(e) => set(["date_label"], e.target.value)} placeholder="Thursday & Friday, 3rd & 4th December 2026" data-testid="cms-datelabel-input" />
        </div>
        <div>
          <Label className="text-xs">Tagline</Label>
          <Textarea className="mt-1" value={c.tagline} onChange={(e) => set(["tagline"], e.target.value)} data-testid="cms-tagline-input" />
        </div>
      </Block>

      <Block title="Section Visibility">
        <p className="text-xs text-[var(--text-muted)] -mt-1">Turn any section on or off. Hidden sections are removed cleanly from the page (no gaps) and drop from the top menu. Each toggle here mirrors the matching toggle in the section editors below.</p>
        <div className="flex flex-wrap gap-2">
          {SECTION_TOGGLES.map((s) => {
            const on = s.type === "sections" ? c.sections?.[s.key] !== false : !!c[s.group]?.enabled;
            const toggle = () =>
              s.type === "sections"
                ? set(["sections", s.key], c.sections?.[s.key] === false)
                : set([s.group, "enabled"], !c[s.group]?.enabled);
            return (
              <button
                key={s.key}
                type="button"
                onClick={toggle}
                data-testid={`cms-section-${s.key}`}
                className="rounded-lg px-4 py-2 text-sm border-2 flex items-center gap-2"
                style={{ borderColor: on ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}
              >
                <span className={`w-2 h-2 rounded-full ${on ? "bg-[var(--gold)]" : "bg-[var(--border)]"}`} />
                <span className="font-medium text-[var(--text-primary)]">{s.label}: {on ? "ON" : "OFF"}</span>
              </button>
            );
          })}
        </div>
      </Block>

      <Block title="Section Order">
        <p className="text-xs text-[var(--text-muted)] -mt-1">Reorder how sections appear on the page. Use the arrows to move a section up or down. Hidden sections keep their place but stay invisible.</p>
        <div className="space-y-2">
          {(c.section_order || defaultSectionOrder).map((key, i) => (
            <div key={key} className="flex items-center justify-between rounded-lg border border-[var(--border)] px-4 py-2 bg-[var(--card)]" data-testid={`cms-order-${key}`}>
              <span className="text-sm font-medium text-[var(--text-primary)]">{i + 1}. {sectionOrderLabels[key] || key}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-2 rounded-md disabled:opacity-30 hover:bg-[var(--surface)]" data-testid={`cms-order-up-${key}`}><ArrowUp className="w-4 h-4 text-[var(--gold-hover)]" /></button>
                <button type="button" onClick={() => moveSection(i, 1)} disabled={i === (c.section_order || defaultSectionOrder).length - 1} className="p-2 rounded-md disabled:opacity-30 hover:bg-[var(--surface)]" data-testid={`cms-order-down-${key}`}><ArrowDown className="w-4 h-4 text-[var(--gold-hover)]" /></button>
              </div>
            </div>
          ))}
        </div>
      </Block>

      <Block title="Hero">
        <ImageUploader label="Hero Background Image" value={c.hero.background_image} onChange={(v) => set(["hero", "background_image"], v)} testid="cms-hero-image" />
        {c.hero.background_image && (
          <div>
            <button
              type="button"
              onClick={() => setHeroAdjust((s) => !s)}
              className="text-xs rounded-lg px-3 py-1.5 border border-[var(--border)] text-[var(--text-secondary)] flex items-center gap-1.5 hover:border-[var(--border-gold)] transition-colors"
              data-testid="cms-hero-adjust-btn"
            >
              <Crop className="w-3.5 h-3.5" /> {heroAdjust ? "Done adjusting" : "Adjust photo — position, zoom & darkening"}
            </button>
            {heroAdjust && (
              <div className="mt-2 space-y-3 rounded-xl border border-[var(--border)] p-3 bg-[var(--surface)]">
                <div>
                  <Label className="text-xs">Drag the photo to move it, slide to zoom — the darkening below shows live right here</Label>
                  <div className="mt-1.5">
                    <ImagePositioner
                      image={imgSrc(c.hero.background_image)}
                      position={c.hero.bg_position}
                      zoom={c.hero.bg_zoom ?? 1}
                      onChange={({ position, zoom }) => { set(["hero", "bg_position"], position); set(["hero", "bg_zoom"], zoom); }}
                      aspectClass="aspect-[16/9]"
                      overlay={c.hero.overlay_opacity ?? 0.55}
                      overlayText={{ subtitle: c.hero.subtitle, names: `${c.couple?.partner1 || "Partner 1"} & ${c.couple?.partner2 || "Partner 2"}` }}
                      testid="cms-hero-positioner"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Photo darkening — for text legibility: {Math.round((c.hero.overlay_opacity ?? 0.55) * 100)}%</Label>
                  <input type="range" min="0.2" max="0.85" step="0.05" value={c.hero.overlay_opacity ?? 0.55} onChange={(e) => set(["hero", "overlay_opacity"], Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" data-testid="cms-hero-overlay" />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Subtitle (script)" value={c.hero.subtitle} onChange={(e) => set(["hero", "subtitle"], e.target.value)} />
          <Field label="Location Label" value={c.hero.location_label} onChange={(e) => set(["hero", "location_label"], e.target.value)} />
          <Field label="Hero 'Directions' Button Label (leave empty to hide)" value={c.hero.directions_label || ""} onChange={(e) => set(["hero", "directions_label"], e.target.value)} placeholder="Venue & Directions" data-testid="cms-hero-directions-label" />
        </div>
        <div>
          <Label className="text-xs">Quick focus presets (optional — or just drag the photo in "Adjust" above)</Label>
          <select className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 text-sm text-[var(--text-primary)]" value={c.hero.bg_position || "center"} onChange={(e) => set(["hero", "bg_position"], e.target.value)} data-testid="cms-hero-position">
            <option value="center">Center</option>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
            <option value="top left">Top left</option>
            <option value="top right">Top right</option>
            <option value="bottom left">Bottom left</option>
            <option value="bottom right">Bottom right</option>
          </select>
        </div>
      </Block>

      <Block title="Our Story">
        <Field label="Section Title" value={c.story.title} onChange={(e) => set(["story", "title"], e.target.value)} />
        <div>
          <Label className="text-xs">Display style</Label>
          <select className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 text-sm text-[var(--text-primary)]" value={c.story.mode || "classic"} onChange={(e) => set(["story", "mode"], e.target.value)} data-testid="cms-story-mode">
            <option value="classic">Classic (two photos + paragraphs)</option>
            <option value="timeline">Scrollable timeline</option>
          </select>
          <p className="text-xs text-[var(--text-muted)] mt-1">Timeline uses the milestones below. Classic uses the photos & paragraphs.</p>
        </div>
        {c.story.mode === "timeline" && (
          <div className="rounded-md border border-[var(--border)] p-3 space-y-3" data-testid="cms-story-timeline-editor">
            <Label className="text-xs">Timeline milestones</Label>
            {(c.story.timeline || []).map((t, i) => (
              <div key={i} className="rounded-lg border border-[var(--border)] p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--text-muted)]">Milestone {i + 1}</span>
                  <button onClick={() => set(["story", "timeline"], c.story.timeline.filter((_, j) => j !== i))} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={t.label || ""} onChange={(e) => set(["story", "timeline", i, "label"], e.target.value)} placeholder="Label (e.g., 2019)" data-testid={`cms-timeline-${i}-label`} />
                  <Input value={t.title || ""} onChange={(e) => set(["story", "timeline", i, "title"], e.target.value)} placeholder="Title (e.g., We met)" data-testid={`cms-timeline-${i}-title`} />
                </div>
                <Textarea value={t.text || ""} onChange={(e) => set(["story", "timeline", i, "text"], e.target.value)} placeholder="What happened in this chapter..." />
                <ImageUploader label="Photo (optional)" value={t.image || ""} onChange={(v) => set(["story", "timeline", i, "image"], v)} testid={`cms-timeline-${i}-image`} positionable aspectClass="aspect-[4/3]" />
              </div>
            ))}
            <button onClick={() => set(["story", "timeline"], [...(c.story.timeline || []), { label: "", title: "", text: "", image: "" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1" data-testid="cms-add-timeline"><Plus className="w-4 h-4" /> Add milestone</button>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <ImageUploader label="Photo 1" value={c.story.image1} onChange={(v) => set(["story", "image1"], v)} testid="cms-story-image1" positionable aspectClass="aspect-[4/5]" />
          <ImageUploader label="Photo 2" value={c.story.image2} onChange={(v) => set(["story", "image2"], v)} testid="cms-story-image2" positionable aspectClass="aspect-[4/5]" />
        </div>
        {c.story.paragraphs.map((p, i) => (
          <div key={i} className="flex gap-2">
            <Textarea value={p} onChange={(e) => set(["story", "paragraphs", i], e.target.value)} />
            <button onClick={() => set(["story", "paragraphs"], c.story.paragraphs.filter((_, j) => j !== i))} className="p-2 text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["story", "paragraphs"], [...c.story.paragraphs, ""])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add paragraph</button>
        <div className="pt-2">
          <Label className="text-xs">More Story Photos (shown as a gallery — pick a shape so varied sizes look great)</Label>
          <div className="mt-2"><PhotoListEditor photos={c.story.photos || []} onChange={(arr) => set(["story", "photos"], arr)} testidPrefix="cms-story-photos" /></div>
        </div>
      </Block>

      <Block title="Rumi's Corner (Your Dog)">
        <button
          type="button"
          onClick={() => set(["rumi", "enabled"], !c.rumi.enabled)}
          data-testid="cms-rumi-enabled"
          className="rounded-lg px-4 py-2 text-sm border-2 w-fit"
          style={{ borderColor: c.rumi.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}
        >
          <span className="font-medium text-[var(--text-primary)]">Rumi section: {c.rumi.enabled ? "ON" : "OFF"}</span>
        </button>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Dog's Name" value={c.rumi.name} onChange={(e) => set(["rumi", "name"], e.target.value)} data-testid="cms-rumi-name" />
          <Field label="Subtitle (script)" value={c.rumi.subtitle} onChange={(e) => set(["rumi", "subtitle"], e.target.value)} />
          <Field label="Section Title" value={c.rumi.title} onChange={(e) => set(["rumi", "title"], e.target.value)} />
        </div>
        <ImageUploader label="Rumi's Photo" value={c.rumi.image} onChange={(v) => set(["rumi", "image"], v)} testid="cms-rumi-image" positionable aspectClass="aspect-square" />
        <div>
          <Label className="text-xs">About Rumi</Label>
          <Textarea className="mt-1" value={c.rumi.description} onChange={(e) => set(["rumi", "description"], e.target.value)} data-testid="cms-rumi-description" />
        </div>
        <div className="pt-2">
          <Label className="text-xs">More Rumi Photos (gallery)</Label>
          <div className="mt-2"><PhotoListEditor photos={c.rumi.photos || []} onChange={(arr) => set(["rumi", "photos"], arr)} testidPrefix="cms-rumi-photos" /></div>
        </div>
      </Block>

      <Block title="Events">
        {c.events.map((ev, i) => (
          <div key={i} className="rounded-lg border border-[var(--border)] p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-xs text-[var(--text-muted)]">Event {i + 1}</span>
              <button onClick={() => set(["events"], c.events.filter((_, j) => j !== i))} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <Field label="Title" value={ev.title} onChange={(e) => set(["events", i, "title"], e.target.value)} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date" type="date" value={ev.date} onChange={(e) => set(["events", i, "date"], e.target.value)} />
              <Field label="Time" value={ev.time} onChange={(e) => set(["events", i, "time"], e.target.value)} />
              <Field label="Venue" value={ev.venue} onChange={(e) => set(["events", i, "venue"], e.target.value)} />
              <Field label="Address" value={ev.address} onChange={(e) => set(["events", i, "address"], e.target.value)} />
              <Field label="Dress Code" value={ev.dress_code || ""} onChange={(e) => set(["events", i, "dress_code"], e.target.value)} placeholder="e.g., Indian festive" data-testid={`cms-event-dresscode-${i}`} />
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] sm:col-span-2 mt-1 cursor-pointer" data-testid={`cms-event-${i}-show-location`}>
                <input type="checkbox" checked={!!ev.show_location} onChange={(e) => set(["events", i, "show_location"], e.target.checked)} className="accent-[var(--gold)] w-4 h-4" />
                Show venue &amp; address on this event card (off by default since the venue is the same for all)
              </label>
            </div>
            <Textarea value={ev.description} onChange={(e) => set(["events", i, "description"], e.target.value)} placeholder="Description" />
            <div className="rounded-md border border-[var(--border)] p-3 space-y-2">
              <Label className="text-xs">Functions / ceremonies on this day</Label>
              {(ev.functions || []).map((fn, k) => (
                <div key={k} className="grid grid-cols-[90px_1fr_1fr_auto] gap-2 items-center">
                  <Input value={fn.time} onChange={(e) => set(["events", i, "functions", k, "time"], e.target.value)} placeholder="Time" data-testid={`cms-event-${i}-fn-${k}-time`} />
                  <Input value={fn.name} onChange={(e) => set(["events", i, "functions", k, "name"], e.target.value)} placeholder="Function" />
                  <Input value={fn.note} onChange={(e) => set(["events", i, "functions", k, "note"], e.target.value)} placeholder="Note (optional)" />
                  <button onClick={() => set(["events", i, "functions"], ev.functions.filter((_, x) => x !== k))} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => set(["events", i, "functions"], [...(ev.functions || []), { time: "", name: "", note: "" }])} className="text-xs text-[var(--gold-hover)] flex items-center gap-1" data-testid={`cms-event-${i}-add-fn`}><Plus className="w-3 h-3" /> Add function</button>
            </div>
          </div>
        ))}
        <button onClick={() => set(["events"], [...c.events, { title: "", date: "", time: "", venue: "", address: "", dress_code: "", description: "" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add event</button>
      </Block>

      <Block title="Venue & Map">
        <p className="text-xs text-[var(--text-muted)] -mt-1">
          For the map: open Google Maps → your venue → Share → "Embed a map" → copy the <b>src</b> URL inside the iframe and paste it below.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Venue Name" value={c.venue.name} onChange={(e) => set(["venue", "name"], e.target.value)} data-testid="cms-venue-name" />
          <Field label="Directions Button Link (Google Maps URL)" value={c.venue.map_link} onChange={(e) => set(["venue", "map_link"], e.target.value)} data-testid="cms-venue-maplink" />
        </div>
        <div>
          <Label className="text-xs">Address</Label>
          <Textarea className="mt-1" value={c.venue.address} onChange={(e) => set(["venue", "address"], e.target.value)} data-testid="cms-venue-address" />
        </div>
        <div>
          <Label className="text-xs">Google Maps Embed URL (iframe src)</Label>
          <Textarea className="mt-1" value={c.venue.map_embed} onChange={(e) => set(["venue", "map_embed"], e.target.value)} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="cms-venue-embed" />
        </div>
      </Block>

      <Block title="RSVP Confirmation Message">
        <p className="text-xs text-[var(--text-muted)] -mt-1">Edit the thank-you message guests see after they accept your invitation.</p>
        <Field label="Confirmation Title" value={c.rsvp_success.title} onChange={(e) => set(["rsvp_success", "title"], e.target.value)} data-testid="cms-rsvp-success-title" />
        <div>
          <Label className="text-xs">Confirmation Message (shown after a guest accepts)</Label>
          <Textarea className="mt-1" value={c.rsvp_success.message} onChange={(e) => set(["rsvp_success", "message"], e.target.value)} data-testid="cms-rsvp-success-message" />
        </div>
      </Block>

      <Block title="Accommodation Note">
        <p className="text-xs text-[var(--text-muted)] -mt-1">Shown in the RSVP form the moment a guest chooses that they need a room. Use it to set expectations (e.g., which nights you cover). Leave blank to hide it.</p>
        <div>
          <Label className="text-xs">Note for guests who need a room</Label>
          <Textarea className="mt-1" value={c.accommodation_info} onChange={(e) => set(["accommodation_info"], e.target.value)} placeholder="e.g., We'll have a room arranged for you between 2 Dec and 4 Dec. Beyond these dates, guests are kindly requested to arrange their own stay." data-testid="cms-accommodation-info" />
        </div>
      </Block>

      <Block title="Travel">
        <Field label="Title" value={c.travel.title} onChange={(e) => set(["travel", "title"], e.target.value)} />
        <Textarea value={c.travel.description} onChange={(e) => set(["travel", "description"], e.target.value)} placeholder="Description" />
        {c.travel.items.map((it, i) => (
          <div key={i} className="grid sm:grid-cols-[160px_1fr_auto] gap-2 items-start">
            <Input value={it.mode} onChange={(e) => set(["travel", "items", i, "mode"], e.target.value)} placeholder="Mode" />
            <Textarea value={it.detail} onChange={(e) => set(["travel", "items", i, "detail"], e.target.value)} placeholder="Detail" />
            <button onClick={() => set(["travel", "items"], c.travel.items.filter((_, j) => j !== i))} className="p-2 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["travel", "items"], [...c.travel.items, { mode: "", detail: "" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add travel option</button>
      </Block>

      <Block title="Community & Open Mic">
        <button
          type="button"
          onClick={() => set(["community", "enabled"], !c.community.enabled)}
          data-testid="cms-community-enabled"
          className="rounded-lg px-4 py-2 text-sm border-2 flex items-center gap-2 w-fit"
          style={{ borderColor: c.community.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}
        >
          <span className="font-medium text-[var(--text-primary)]">Open-mic section: {c.community.enabled ? "ON" : "OFF"}</span>
        </button>
        <p className="text-xs text-[var(--text-muted)]">When OFF, the community section and the RSVP open-mic question are hidden.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section Title" value={c.community.title} onChange={(e) => set(["community", "title"], e.target.value)} data-testid="cms-community-title" />
          <Field label="Subtitle (script)" value={c.community.subtitle} onChange={(e) => set(["community", "subtitle"], e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea className="mt-1" value={c.community.description} onChange={(e) => set(["community", "description"], e.target.value)} data-testid="cms-community-description" />
        </div>
        <div>
          <Label className="text-xs">Nudge line</Label>
          <Textarea className="mt-1" value={c.community.nudge} onChange={(e) => set(["community", "nudge"], e.target.value)} />
        </div>
        <Label className="text-xs">Activity chips</Label>
        {c.community.activities.map((a, i) => (
          <div key={i} className="flex gap-2">
            <Input value={a} onChange={(e) => set(["community", "activities", i], e.target.value)} placeholder="e.g., Play the guitar" />
            <button onClick={() => set(["community", "activities"], c.community.activities.filter((_, j) => j !== i))} className="p-2 text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["community", "activities"], [...c.community.activities, ""])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add activity</button>
      </Block>

      <Block title="Guestbook Wall">
        <button type="button" onClick={() => set(["guestbook", "enabled"], !c.guestbook.enabled)} data-testid="cms-guestbook-enabled" className="rounded-lg px-4 py-2 text-sm border-2 w-fit" style={{ borderColor: c.guestbook.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}>
          <span className="font-medium text-[var(--text-primary)]">Guestbook section: {c.guestbook.enabled ? "ON" : "OFF"}</span>
        </button>
        <p className="text-xs text-[var(--text-muted)]">A public wall where guests leave a short blessing for you both.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section Title" value={c.guestbook.title} onChange={(e) => set(["guestbook", "title"], e.target.value)} data-testid="cms-guestbook-title" />
          <Field label="Subtitle (script)" value={c.guestbook.subtitle} onChange={(e) => set(["guestbook", "subtitle"], e.target.value)} />
        </div>
        <div><Label className="text-xs">Description</Label><Textarea className="mt-1" value={c.guestbook.description} onChange={(e) => set(["guestbook", "description"], e.target.value)} data-testid="cms-guestbook-description" /></div>
        <button type="button" onClick={() => set(["guestbook", "moderation"], !c.guestbook.moderation)} data-testid="cms-guestbook-moderation" className="rounded-lg px-4 py-2 text-sm border-2 w-fit" style={{ borderColor: c.guestbook.moderation ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}>
          <span className="font-medium text-[var(--text-primary)]">Approve wishes before they show: {c.guestbook.moderation ? "ON" : "OFF"}</span>
        </button>
        <p className="text-xs text-[var(--text-muted)]">When ON, wishes stay hidden until you approve them in the Guestbook tab.</p>
      </Block>

      <Block title="Community Events (idea wall)">
        <button type="button" onClick={() => set(["suggestions", "enabled"], !c.suggestions.enabled)} data-testid="cms-suggestions-enabled" className="rounded-lg px-4 py-2 text-sm border-2 w-fit" style={{ borderColor: c.suggestions.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}>
          <span className="font-medium text-[var(--text-primary)]">Community Events section: {c.suggestions.enabled ? "ON" : "OFF"}</span>
        </button>
        <p className="text-xs text-[var(--text-muted)]">Guests suggest open-mic acts or activities — the celebration builds itself.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Section Title" value={c.suggestions.title} onChange={(e) => set(["suggestions", "title"], e.target.value)} data-testid="cms-suggestions-title" />
          <Field label="Subtitle (script)" value={c.suggestions.subtitle} onChange={(e) => set(["suggestions", "subtitle"], e.target.value)} />
        </div>
        <div><Label className="text-xs">Description</Label><Textarea className="mt-1" value={c.suggestions.description} onChange={(e) => set(["suggestions", "description"], e.target.value)} data-testid="cms-suggestions-description" /></div>
        <button type="button" onClick={() => set(["suggestions", "moderation"], !c.suggestions.moderation)} data-testid="cms-suggestions-moderation" className="rounded-lg px-4 py-2 text-sm border-2 w-fit" style={{ borderColor: c.suggestions.moderation ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}>
          <span className="font-medium text-[var(--text-primary)]">Approve ideas before they show: {c.suggestions.moderation ? "ON" : "OFF"}</span>
        </button>
        <p className="text-xs text-[var(--text-muted)]">When ON, ideas stay hidden until you approve them in the Community Ideas tab.</p>
      </Block>

      <Block title="Hashtag & Page Title">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Wedding Hashtag" value={c.hashtag || ""} onChange={(e) => set(["hashtag"], e.target.value)} placeholder="#RishiWedsPriyanka" data-testid="cms-hashtag-input" />
          <Field label="Browser / SEO Title" value={c.meta_title || ""} onChange={(e) => set(["meta_title"], e.target.value)} placeholder="Rishi & Priyanka · 3-4 Dec 2026" data-testid="cms-metatitle-input" />
        </div>
      </Block>

      <Block title="FAQ / Good to Know">
        <button
          type="button"
          onClick={() => set(["faq", "enabled"], !c.faq.enabled)}
          data-testid="cms-faq-enabled"
          className="rounded-lg px-4 py-2 text-sm border-2 w-fit"
          style={{ borderColor: c.faq.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}
        >
          <span className="font-medium text-[var(--text-primary)]">FAQ section: {c.faq.enabled ? "ON" : "OFF"}</span>
        </button>
        <Field label="Section Title" value={c.faq.title} onChange={(e) => set(["faq", "title"], e.target.value)} />
        {c.faq.items.map((it, i) => (
          <div key={i} className="rounded-lg border border-[var(--border)] p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-[var(--text-muted)]">Q{i + 1}</span>
              <button onClick={() => set(["faq", "items"], c.faq.items.filter((_, j) => j !== i))} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <Input value={it.q} onChange={(e) => set(["faq", "items", i, "q"], e.target.value)} placeholder="Question" data-testid={`cms-faq-q-${i}`} />
            <Textarea value={it.a} onChange={(e) => set(["faq", "items", i, "a"], e.target.value)} placeholder="Answer" />
          </div>
        ))}
        <button onClick={() => set(["faq", "items"], [...c.faq.items, { q: "", a: "" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add question</button>
      </Block>

      <Block title="Contacts (WhatsApp)">
        <button
          type="button"
          onClick={() => set(["contacts", "enabled"], !c.contacts.enabled)}
          data-testid="cms-contacts-enabled"
          className="rounded-lg px-4 py-2 text-sm border-2 w-fit"
          style={{ borderColor: c.contacts.enabled ? "var(--gold)" : "var(--border)", backgroundColor: "var(--card)" }}
        >
          <span className="font-medium text-[var(--text-primary)]">Contacts in footer: {c.contacts.enabled ? "ON" : "OFF"}</span>
        </button>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title" value={c.contacts.title} onChange={(e) => set(["contacts", "title"], e.target.value)} />
          <Field label="Note" value={c.contacts.note} onChange={(e) => set(["contacts", "note"], e.target.value)} />
        </div>
        {c.contacts.people.map((p, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1fr_140px_auto] gap-2 items-end">
            <div><Label className="text-xs">Name</Label><Input className="mt-1" value={p.name} onChange={(e) => set(["contacts", "people", i, "name"], e.target.value)} /></div>
            <div><Label className="text-xs">WhatsApp</Label><Input className="mt-1" value={p.whatsapp} onChange={(e) => set(["contacts", "people", i, "whatsapp"], e.target.value)} placeholder="+91 ..." data-testid={`cms-contact-wa-${i}`} /></div>
            <div><Label className="text-xs">Role</Label><Input className="mt-1" value={p.role} onChange={(e) => set(["contacts", "people", i, "role"], e.target.value)} placeholder="Groom / Family" /></div>
            <button onClick={() => set(["contacts", "people"], c.contacts.people.filter((_, j) => j !== i))} className="p-2 text-red-500 h-10"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["contacts", "people"], [...c.contacts.people, { name: "", whatsapp: "", role: "" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add contact</button>
      </Block>

      <Block title="Branding, Fonts & Colors">
        <div>
          <Label className="text-xs">Font Pairing</Label>
          <select className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 text-sm text-[var(--text-primary)]" value={c.theme.font_pairing || "pairing_1"} onChange={(e) => set(["theme", "font_pairing"], e.target.value)} data-testid="cms-font-pairing">
            {FONT_PAIRINGS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <p className="text-xs text-[var(--text-muted)]">Colors control the site's light-mode palette (applied live). Dark mode keeps its own matching palette.</p>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="cms-branding">
          <ColorField label="Primary / Gold" value={c.theme.primary} onChange={(v) => set(["theme", "primary"], v)} />
          <ColorField label="Secondary / Burgundy" value={c.theme.secondary} onChange={(v) => set(["theme", "secondary"], v)} />
          <ColorField label="Background" value={c.theme.background} onChange={(v) => set(["theme", "background"], v)} />
          <ColorField label="Surface" value={c.theme.surface} onChange={(v) => set(["theme", "surface"], v)} />
          <ColorField label="Accent / Rose" value={c.theme.accent} onChange={(v) => set(["theme", "accent"], v)} />
        </div>
      </Block>

      <Block title="Custom RSVP Questions">
        <p className="text-xs text-[var(--text-muted)] -mt-1">Add your own questions to the RSVP form. Answers appear in the dashboard & CSV.</p>
        {c.rsvp_extra_fields.map((f, i) => (
          <div key={f.id || i} className="grid sm:grid-cols-[1fr_150px_auto] gap-2 items-end">
            <div>
              <Label className="text-xs">Question</Label>
              <Input className="mt-1" value={f.label} onChange={(e) => set(["rsvp_extra_fields", i, "label"], e.target.value)} placeholder="e.g., Any song requests?" data-testid={`cms-customq-${i}`} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 text-sm text-[var(--text-primary)]" value={f.type} onChange={(e) => set(["rsvp_extra_fields", i, "type"], e.target.value)}>
                <option value="text">Short text</option>
                <option value="textarea">Long text</option>
              </select>
            </div>
            <button onClick={() => set(["rsvp_extra_fields"], c.rsvp_extra_fields.filter((_, j) => j !== i))} className="p-2 text-red-500 h-10"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["rsvp_extra_fields"], [...c.rsvp_extra_fields, { id: "q" + Date.now(), label: "", type: "text" }])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1" data-testid="cms-add-customq"><Plus className="w-4 h-4" /> Add question</button>
      </Block>

      <Block title="Gallery Images">
        {c.gallery.map((src, i) => (
          <div key={i} className="flex gap-2 items-start">
            <div className="flex-1">
              <ImageUploader value={src} onChange={(v) => set(["gallery", i], v)} testid={`cms-gallery-${i}`} positionable aspectClass="aspect-square" />
            </div>
            <button onClick={() => set(["gallery"], c.gallery.filter((_, j) => j !== i))} className="p-2 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => set(["gallery"], [...c.gallery, ""])} className="text-sm text-[var(--gold-hover)] flex items-center gap-1"><Plus className="w-4 h-4" /> Add image</button>
      </Block>

      <div className="sticky bottom-4 z-30 flex justify-center" data-testid="cms-action-bar">
        <div className={`flex flex-wrap items-center justify-center gap-2 rounded-full border-2 px-3 py-2 shadow-xl ${hasChanges ? "border-[var(--border-gold)] bg-[var(--card)]" : "border-[var(--border)] bg-[var(--card)]"}`}>
          {hasChanges && (
            <span className="hidden sm:inline text-xs font-medium text-[var(--burgundy)] pl-2 pr-1" data-testid="cms-changed-count">
              {changed.length} unpublished change{changed.length === 1 ? "" : "s"}
            </span>
          )}
          <button onClick={save} disabled={saving} className="rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 shadow disabled:opacity-60" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="cms-save-content">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save draft"}
          </button>
          {hasChanges && (
            <>
              <button onClick={openPreview} className="rounded-full px-4 py-2.5 text-sm font-medium border border-[var(--border)] text-[var(--text-primary)] flex items-center gap-1.5 hover:border-[var(--border-gold)]" data-testid="cms-preview-toggle-bottom">
                <Eye className="w-4 h-4" /> Preview
              </button>
              <button onClick={() => setConfirmOpen(true)} disabled={publishing} className="rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-1.5 text-white disabled:opacity-40 shadow" style={{ backgroundColor: "var(--burgundy)" }} data-testid="cms-publish-btn-bottom">
                <Send className="w-4 h-4" /> Publish
              </button>
            </>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cms-publish-confirm">
          <div className="bg-[var(--card)] rounded-2xl p-6 max-w-md w-full border border-[var(--border)] shadow-xl">
            <h3 className="font-serif text-2xl text-[var(--burgundy)]">Publish to the live site?</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">Your saved draft will replace what guests currently see. Preview it first to be sure everything looks right.</p>
            <button onClick={() => { setConfirmOpen(false); openPreview(); }} className="mt-4 inline-flex items-center gap-1.5 text-sm text-[var(--gold-hover)] underline" data-testid="cms-preview-link"><Eye className="w-4 h-4" /> Preview draft first</button>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-full border border-[var(--border)] text-sm text-[var(--text-secondary)]" data-testid="cms-publish-cancel">Cancel</button>
              <button onClick={publish} disabled={publishing} className="px-5 py-2 rounded-full text-sm font-medium disabled:opacity-60" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="cms-publish-confirm-btn">{publishing ? "Publishing..." : "Publish now"}</button>
            </div>
          </div>
        </div>
      )}

      {discardOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cms-discard-confirm">
          <div className="bg-[var(--card)] rounded-2xl p-6 max-w-md w-full border border-[var(--border)] shadow-xl">
            <h3 className="font-serif text-2xl text-[var(--burgundy)]">Discard draft changes?</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">This throws away all unpublished edits and resets the editor to exactly what your guests currently see on the live site. This can't be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDiscardOpen(false)} className="px-4 py-2 rounded-full border border-[var(--border)] text-sm text-[var(--text-secondary)]" data-testid="cms-discard-cancel">Keep editing</button>
              <button onClick={discard} disabled={discarding} className="px-5 py-2 rounded-full text-sm font-medium text-white bg-red-500 disabled:opacity-60" data-testid="cms-discard-confirm-btn">{discarding ? "Discarding..." : "Discard changes"}</button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex flex-col" data-testid="cms-preview-overlay">
          <div className="bg-[var(--card)] px-4 py-3 flex items-center justify-between border-b border-[var(--border)]">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-[var(--gold)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">Draft preview — shows your last saved draft (not visible to guests)</span>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => setPreviewKey((k) => k + 1)} className="text-sm text-[var(--gold-hover)] flex items-center gap-1" data-testid="cms-preview-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
              <a href={previewUrl} target="_blank" rel="noreferrer" className="text-sm text-[var(--gold-hover)] flex items-center gap-1" data-testid="cms-preview-newtab">New tab <ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => setPreviewOpen(false)} className="text-sm text-[var(--text-secondary)] flex items-center gap-1" data-testid="cms-preview-close"><X className="w-4 h-4" /> Close</button>
            </div>
          </div>
          <iframe key={previewKey} title="Draft preview" src={previewUrl} className="flex-1 w-full bg-white" data-testid="cms-preview-iframe" />
        </div>
      )}
    </div>
  );
};
